import { cyrb64Hash } from "@/lib/crypto.utils"
import { InteractionSchema, type TContact, type TInteraction } from "@/schemas/contacts"
import type { TPhoneStatus } from "@/schemas/user.schema"
import { observable } from "@legendapp/state"

interface IChatData {
  unreadChats: Record<TContact["ContactPersonId"], number> // For now number is just 0 or 1
  interactions: Record<TContact["ContactPersonId"], Record<string, TInteraction>> // TInteraction["RingId"]
  sortedInteractions: Record<TContact["ContactPersonId"], Array<string>> // TInteraction["RingId"]
  contacts: Record<string, TContact>
  liveCalls: Array<{
    contactId: TContact["ContactPersonId"]
    ringId: TInteraction["RingId"]
    status: TPhoneStatus
    direction: TInteraction["Direction"]
  }>
  chats: Array<TContact["ContactPersonId"]>
  chatLastInteraction: Record<TContact["ContactPersonId"], TInteraction>
  searchChats: Array<TContact["ContactPersonId"]>
  filteredChats: Array<TContact["ContactPersonId"]>
  _internal: {
    // This allow us to have a map with only unique keys
    // Like a Set, but with the ability to have a key-value pair
    chatsMap: Record<TContact["ContactPersonId"], boolean>
    searchChatsMap: Record<TContact["ContactPersonId"], boolean>
    liveCallsMap: Record<TContact["ContactPersonId"], boolean>

    // Having a map of sortedInteractions allows us to determine if an interaction
    // has already been added to the sorted array in O(1) time
    sortedInteractionsMap: Record<TContact["ContactPersonId"], Record<string, boolean>>
  }
}

const initialState: IChatData = {
  unreadChats: {},
  interactions: {},
  sortedInteractions: {},
  contacts: {},
  liveCalls: [],
  chats: [],
  chatLastInteraction: {},
  searchChats: [],
  filteredChats: [],
  _internal: {
    chatsMap: {},
    searchChatsMap: {},
    liveCallsMap: {},
    sortedInteractionsMap: {},
  },
}

interface IChatStore extends IChatData {}

export const chatStore$ = observable<IChatStore>({
  ...initialState,
})

function normalizeContactField(value: unknown): string | undefined {
  if (typeof value === "string") return value
  if (Array.isArray(value)) {
    const firstString = value.find((item) => typeof item === "string")
    return typeof firstString === "string" ? firstString : undefined
  }
  return undefined
}

function normalizeDirection(value: unknown): TInteraction["Direction"] {
  return value === "in" || value === "ou" ? value : undefined
}

export function buildFallbackInteractionFromContact(contact: TContact): TInteraction {
  const interaction: TInteraction = {
    AccountId: contact.AccountId,
    Body: contact.Note ?? undefined,
    CompanyName: contact.ContactCompanyName,
    ContactCompanyName: contact.ContactCompanyName,
    ContactEmails: normalizeContactField(contact.ContactEmails),
    ContactFirstname: contact.ContactFirstname,
    ContactFullname: contact.ContactFullname,
    ContactLastNote: typeof contact.ContactLastNote === "string" ? contact.ContactLastNote : contact.Note ?? undefined,
    ContactLastname: contact.ContactLastname,
    ContactPersonId: contact.ContactPersonId,
    ContactPhoneNumber: contact.ContactPhoneNumber,
    ContactPhoneNumbers: normalizeContactField(contact.ContactPhoneNumbers),
    ContactRemoteJson: contact.ContactRemoteJson,
    Direction: normalizeDirection(contact.Direction),
    Hash: contact.Hash ?? cyrb64Hash(`${contact.AccountId}:${contact.ContactPersonId}:${contact.LastTimestamp}`),
    NeedsMyAttention: typeof contact.NeedsMyAttention === "number" ? contact.NeedsMyAttention : undefined,
    Origin: typeof contact.Origin === "string" ? contact.Origin : undefined,
    Outcome: typeof contact.Outcome === "string" ? contact.Outcome : undefined,
    RingId: typeof contact.RingId === "string" ? contact.RingId : contact.Hash ?? contact.ContactPersonId,
    StartTimestamp: typeof contact.StartTimestamp === "number" ? contact.StartTimestamp : contact.LastTimestamp,
    Status: typeof contact.Status === "string" ? contact.Status : undefined,
    TTL: contact.TTL,
    Type: typeof contact.Type === "string" ? contact.Type : "unknown",
    UserFullname: typeof contact.UserFullname === "string" ? contact.UserFullname : undefined,
    UserPersonId: typeof contact.UserPersonId === "string" ? contact.UserPersonId : undefined,
  }

  return interaction
}

export function extractInteractionFromContact(contact: TContact): TInteraction {
  const parsedInteraction = InteractionSchema.safeParse(contact)
  const interaction = parsedInteraction.success ? parsedInteraction.data : buildFallbackInteractionFromContact(contact)
  interaction.Hash = interaction.Hash ?? cyrb64Hash(JSON.stringify(interaction))

  return interaction
}
